import { getLang } from '@translate';

export type FormatNumberOptions = {
    maximumFractionDigits?: number;
    locale?: string;
};

const DEFAULT_MAX_FRACTION_DIGITS = 6;

export const getLocale = (): string => getLang();

export const getDecimalSeparator = (locale?: string): string => {
    const loc = locale ?? getLocale();
    const parts = new Intl.NumberFormat(loc).formatToParts(1.1);
    return parts.find((part) => part.type === 'decimal')?.value ?? '.';
};

export const getGroupSeparator = (locale?: string): string => {
    const loc = locale ?? getLocale();
    const parts = new Intl.NumberFormat(loc).formatToParts(1000);
    return parts.find((part) => part.type === 'group')?.value ?? ',';
};

export const isNumericValue = (value: unknown): value is number | string => {
    if (value === null || value === undefined || value === '') {
        return false;
    }
    if (typeof value === 'number') {
        return !Number.isNaN(value);
    }
    return !Number.isNaN(Number(value));
};

export const formatNumber = (value: unknown, options?: FormatNumberOptions): string => {
    if (!isNumericValue(value)) {
        return '';
    }

    const num = typeof value === 'number' ? value : Number(value);
    if (Number.isNaN(num)) {
        return '';
    }

    const locale = options?.locale ?? getLocale();
    const maximumFractionDigits = options?.maximumFractionDigits ?? DEFAULT_MAX_FRACTION_DIGITS;

    let formatted = new Intl.NumberFormat(locale, {
        useGrouping: true,
        maximumFractionDigits,
    }).format(num);

    if (locale === 'cs-CZ') {
        formatted = formatted.replace(/[\u00A0\u202F]/g, ' ');
    }

    return formatted;
};

export const parseLocaleNumber = (input: string | number | undefined | null, locale?: string): number => {
    if (input === null || input === undefined || input === '') {
        return NaN;
    }
    if (typeof input === 'number') {
        return input;
    }

    let normalized = String(input).trim();
    if (normalized === '' || normalized === '-') {
        return NaN;
    }

    normalized = normalized.replace(/[\s\u00A0\u202F]/g, '');

    const loc = locale ?? getLocale();
    const groupSep = getGroupSeparator(loc);
    if (groupSep) {
        const escapedGroup = groupSep.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
        normalized = normalized.replace(new RegExp(escapedGroup, 'g'), '');
    }

    const lastComma = normalized.lastIndexOf(',');
    const lastDot = normalized.lastIndexOf('.');

    if (lastComma !== -1 && lastDot !== -1) {
        if (lastComma > lastDot) {
            normalized = normalized.replace(/\./g, '').replace(',', '.');
        } else {
            normalized = normalized.replace(/,/g, '');
        }
    } else if (lastComma !== -1) {
        normalized = normalized.replace(',', '.');
    }

    return Number(normalized);
};
